'use client';

import { Fragment, useEffect, useRef, useState } from 'react';
import Image from 'next/image';
import classes from './DeliveryVideoSection.module.scss';
import { IconPlayerPlay, IconX } from '@tabler/icons-react';
import { createPortal } from 'react-dom';
import { gaClick, gaCta } from '@/src/helpers/gtag';
import { useAOS } from '@/src/lib/hooks/useAOS';

export interface DeliveryVideoSectionProps {
  eyebrow?: string;
  title?: string;
  description?: string;
  ctaLabel?: string;
  ctaHref?: string;
  ctaHint?: string;
  secondaryCtaLabel?: string;
  secondaryCtaHref?: string;
  videoUrl: string;
  posterUrl?: string;
  videoAlt?: string;
  step?: number;
}

const renderTitle = (title: string) => {
  const parts = title.split('*');
  return parts.map((part, i) => {
    if (i % 2 === 1) {
      return <em key={i}>{part}</em>;
    }
    const segments = part.split('—');
    return segments.map((segment, j) => (
      <Fragment key={j}>
        {segment}
        {j < segments.length - 1 && <span className={classes.titleDash}>—</span>}
      </Fragment>
    ));
  });
};

export const DeliveryVideoSection = ({
  eyebrow,
  title,
  description,
  ctaLabel,
  ctaHref,
  ctaHint,
  secondaryCtaLabel,
  secondaryCtaHref,
  videoUrl,
  posterUrl,
  videoAlt = '',
  step,
}: DeliveryVideoSectionProps) => {
  useAOS();

  const [isOpen, setIsOpen] = useState(false);
  const [canPortal, setCanPortal] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => setCanPortal(true), []);

  const open = () => {
    gaClick('delivery_video_play');
    setIsOpen(true);
  };
  const close = () => setIsOpen(false);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        close();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen]);

  const modal = isOpen ? (
    <div className={classes.modalOverlay}>
      <div className={classes.modalBackdrop} aria-hidden="true" onClick={close} />
      <div className={classes.modal} role="dialog" aria-modal="true" aria-label={videoAlt}>
        <button
          className={classes.modalClose}
          onClick={close}
          aria-label="Fermer la vidéo"
          type="button"
        >
          <IconX size={20} />
        </button>
        <div className={classes.modalVideoContainer}>
          <video
            ref={videoRef}
            className={classes.modalVideo}
            controls
            autoPlay
            playsInline
            preload="auto"
          >
            <source src={videoUrl} type="video/mp4" />
            <track kind="captions" />
            Votre navigateur ne supporte pas la lecture de vidéos.
          </video>
        </div>
      </div>
    </div>
  ) : null;

  return (
    <section className={classes.section} aria-labelledby="delivery-video-title">
      <div className={classes.container}>
        {(eyebrow || title) && (
          <div className={classes.header}>
            {eyebrow && (
              <div className={classes.eyebrowRow}>
                {step && (
                  <span
                    className={classes.stepBadge}
                    data-aos="fade-down"
                    data-aos-duration="600"
                    data-aos-once="true"
                  >
                    {step}
                  </span>
                )}
                <p className={classes.eyebrow}>{eyebrow}</p>
              </div>
            )}
            {title && (
              <h2 id="delivery-video-title" className={classes.title}>
                {renderTitle(title)}
              </h2>
            )}
          </div>
        )}

        <div className={classes.videoWrapper}>
          <div className={classes.textColumn}>
            {description && <p className={classes.leadDescription}>{description}</p>}
            {ctaLabel && ctaHref && (
              <div className={classes.ctaWrapper}>
                <a
                  href={ctaHref}
                  className={classes.ctaButton}
                  onClick={() => gaCta(ctaLabel, 'delivery_video_cta', ctaHref)}
                >
                  {ctaLabel}
                  <span aria-hidden="true">→</span>
                </a>
                {ctaHint && <p className={classes.ctaHint}>{ctaHint}</p>}
              </div>
            )}
            {secondaryCtaLabel && secondaryCtaHref && (
              <a
                href={secondaryCtaHref}
                className={classes.secondaryCta}
                onClick={() =>
                  gaCta(secondaryCtaLabel, 'delivery_video_secondary_cta', secondaryCtaHref)
                }
              >
                {secondaryCtaLabel}
              </a>
            )}
          </div>

          <div className={classes.videoColumn}>
            <div
              className={classes.videoContainer}
              role="button"
              tabIndex={0}
              onClick={open}
              onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && open()}
              aria-label="Ouvrir la vidéo"
            >
              {posterUrl ? (
                <Image
                  src={posterUrl}
                  alt={videoAlt}
                  fill
                  sizes="(max-width: 64em) 100vw, 50vw"
                  className={classes.posterNextImage}
                  priority={false}
                />
              ) : (
                <div className={classes.fallbackPoster} aria-hidden="true" />
              )}
              <div className={classes.posterOverlay}>
                <button
                  type="button"
                  className={classes.playButton}
                  onClick={open}
                  aria-label="Lire la vidéo"
                >
                  <IconPlayerPlay size={48} stroke={2} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {canPortal && modal ? createPortal(modal, document.body) : null}
    </section>
  );
};
